import { useRef, useState } from "react";
import { Camera, Loader2, X, Film, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage, isImageFile, isVideoFile } from "@/lib/imageCompress";
import { uploadMedia, readMediaDuration } from "@/lib/upload";
import { mediaUrl } from "@/lib/api";

const VIDEO_MAX_MB = 50;
const VIDEO_MAX_S = 60;
const fmtMB = (b) => `${(b / 1024 ** 2).toFixed(1)} MB`;

/** Citizen attachment: compresses photos on-device (≤1600px, JPEG 80%, HEIC→JPEG),
 *  validates videos (≤50 MB / 60 s), uploads with progress, falls back to original on compress failure.
 *  onChange({ url, kind, file_id } | null) */
const CitizenAttachment = ({ accept = "image/*", allowVideo = false, value, onChange, testId, label }) => {
  const [state, setState] = useState(null); // {pct, label}
  const [preview, setPreview] = useState(null);
  const [info, setInfo] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setPreview(null);
    setInfo(null);
    setState(null);
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const video = isVideoFile(file);
    const image = isImageFile(file);
    if (!image && !(allowVideo && video)) {
      toast.error(allowVideo ? "Envie uma foto ou um vídeo." : "Envie uma foto (JPG, PNG, HEIC).");
      e.target.value = "";
      return;
    }
    try {
      let toSend = file;
      let duration = null;
      if (video) {
        if (file.size > VIDEO_MAX_MB * 1024 ** 2) throw new Error(`Vídeo com ${fmtMB(file.size)}. O limite é ${VIDEO_MAX_MB} MB — grave um trecho mais curto ou reduza a qualidade na câmera.`);
        setState({ pct: 0, label: "Verificando vídeo…" });
        duration = await readMediaDuration(file);
        if (duration && duration > VIDEO_MAX_S) throw new Error(`Vídeo com ${duration}s. O limite é ${VIDEO_MAX_S} segundos — corte o trecho mais importante.`);
        setPreview({ kind: "video", src: URL.createObjectURL(file) });
      } else {
        setState({ pct: 0, label: "Comprimindo foto…" });
        try {
          toSend = await compressImage(file, { onProgress: (p) => setState({ pct: Math.round(p * 0.3), label: "Comprimindo foto…" }) });
        } catch {
          if (file.size > 8 * 1024 ** 2) throw new Error("Não foi possível comprimir a foto e ela excede 8 MB. Tente outra imagem.");
          toSend = file; // fallback: send original within limit
        }
        setPreview({ kind: "image", src: URL.createObjectURL(toSend) });
      }
      setState({ pct: video ? 0 : 30, label: "Enviando…" });
      const res = await uploadMedia(toSend, {
        scope: "citizen",
        durationSeconds: duration,
        onProgress: (p) => setState({ pct: video ? p : 30 + Math.round(p * 0.7), label: "Enviando…" }),
      });
      setInfo({ original: file.size, sent: toSend.size, kind: video ? "video" : "image", processing: res.processing });
      setState(null);
      onChange({ url: res.url, kind: video ? "video" : "image", file_id: res.file_id });
    } catch (err) {
      const msg = err?.response?.data?.detail
        ? (typeof err.response.data.detail === "string" ? err.response.data.detail : "Arquivo recusado pelo servidor.")
        : err?.message || "Falha no envio. Verifique sua conexão e tente de novo.";
      toast.error(msg, { duration: 8000 });
      reset();
    }
  };

  return (
    <div data-testid={testId}>
      {!value && !state && (
        <label className="flex items-center gap-3 rounded-xl border border-dashed border-gray-300 hover:border-[var(--fi-blue)] p-4 cursor-pointer transition-colors">
          <Camera className="w-5 h-5 text-[var(--fi-blue)]" />
          <div className="text-sm text-gray-600">
            <div>{label}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              Fotos são reduzidas no aparelho antes do envio{allowVideo && ` · vídeo até ${VIDEO_MAX_MB} MB e ${VIDEO_MAX_S} s`}
            </div>
          </div>
          <input ref={inputRef} type="file" accept={allowVideo ? "image/*,video/*" : accept} onChange={onPick} className="hidden" data-testid={`${testId}-input`} />
        </label>
      )}
      {state && (
        <div className="rounded-xl border border-gray-200 p-4" data-testid={`${testId}-progress`}>
          <div className="flex justify-between text-xs font-bold text-[var(--fi-blue)] mb-1.5">
            <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />{state.label}</span>
            <span>{state.pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[var(--fi-red)] transition-[width] duration-200" style={{ width: `${state.pct}%` }} /></div>
        </div>
      )}
      {value && !state && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 flex items-center gap-3" data-testid={`${testId}-done`}>
          {preview?.kind === "image" && <img src={preview.src || mediaUrl(value.url)} alt="Prévia" className="w-16 h-16 rounded-lg object-cover" />}
          {preview?.kind === "video" && <div className="w-16 h-16 rounded-lg bg-black/80 flex items-center justify-center text-white"><Film className="w-6 h-6" /></div>}
          <div className="flex-1 min-w-0 text-xs text-gray-600">
            <div className="inline-flex items-center gap-1 font-bold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Anexo pronto</div>
            {info && (
              <div className="mt-0.5">
                {info.kind === "image" ? `Foto: ${fmtMB(info.original)} → ${fmtMB(info.sent)}` : `Vídeo: ${fmtMB(info.original)}${info.processing ? " · será comprimido pela redação automaticamente" : ""}`}
              </div>
            )}
          </div>
          <button type="button" onClick={reset} className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center text-gray-500" aria-label="Remover anexo" data-testid={`${testId}-remove`}><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

export default CitizenAttachment;
